import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2, Plus, Package, Users, ClipboardList, Mail, LayoutDashboard, ShieldAlert, Globe, ShieldCheck, Bell, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const emptyProduct = { name: "", category: "eBooks", type: "ebook", price: "", image: "", short: "", description: "", download_url: "", badge: "" };
const emptyCaregiver = { name: "", specialties: "", location: "Columbus, OH", hourly_rate: 25, years: 1, rating: 5, reviews: 0, image: "", bio: "" };

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pForm, setPForm] = useState(emptyProduct);
  const [cForm, setCForm] = useState(emptyCaregiver);
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(() => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/caregivers").then((r) => setCaregivers(r.data)).catch(() => {});
    api.get("/admin/care-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/admin/contacts").then((r) => setMessages(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    loadAll();
  }, [user, loading, navigate, loadAll]);

  if (loading || !user || user.role !== "admin")
    return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const createProduct = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/products", { ...pForm, price: parseFloat(pForm.price || 0), badge: pForm.badge || null });
      toast.success("Product added"); setPForm(emptyProduct); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add product"); }
    finally { setSaving(false); }
  };
  const deleteProduct = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Deleted"); loadAll(); };

  const createCaregiver = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/admin/caregivers", { ...cForm, hourly_rate: parseInt(cForm.hourly_rate) || 25, years: parseInt(cForm.years) || 1, rating: parseFloat(cForm.rating) || 5, reviews: parseInt(cForm.reviews) || 0, specialties: cForm.specialties.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Caregiver added"); setCForm(emptyCaregiver); loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add caregiver"); }
    finally { setSaving(false); }
  };
  const deleteCaregiver = async (id) => { await api.delete(`/admin/caregivers/${id}`); toast.success("Deleted"); loadAll(); };

  const stats = overview ? [
    { icon: Package, label: "Products", value: overview.products },
    { icon: Users, label: "Caregivers", value: overview.caregivers },
    { icon: ClipboardList, label: "Care Requests", value: overview.care_requests },
    { icon: Mail, label: "Messages", value: overview.contacts },
    { icon: LayoutDashboard, label: "Paid Orders", value: overview.orders },
  ] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-8">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-900 text-white"><ShieldAlert className="w-6 h-6" /></span>
        <div className="flex-1"><h1 className="font-display text-3xl font-bold text-blue-950">Admin Dashboard</h1><p className="text-slate-500">Manage your platform, {user.name}</p></div>
        <Button data-testid="command-center-link" onClick={() => navigate("/command")} className="bg-blue-900 hover:bg-blue-950 text-white gap-2"><Brain className="w-4 h-4" /> Command Center</Button>
        <Button data-testid="national-map-link" onClick={() => navigate("/admin/map")} className="bg-teal-600 hover:bg-teal-700 text-white gap-2"><Globe className="w-4 h-4" /> National Map</Button>
        <Button data-testid="admin-compliance-link" onClick={() => navigate("/admin/compliance")} variant="outline" className="border-blue-900/20 text-blue-900 gap-2"><ShieldCheck className="w-4 h-4" /> Regulatory Data</Button>
        <Button data-testid="admin-changes-link" onClick={() => navigate("/admin/changes")} variant="outline" className="border-blue-900/20 text-blue-900 gap-2"><Bell className="w-4 h-4" /> Change Monitor</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-10">
        {stats.map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 border border-blue-900/5 shadow-sm">
            <s.icon className="w-6 h-6 text-blue-700 mb-2" />
            <p className="text-2xl font-display font-bold text-blue-950">{s.value}</p>
            <p className="text-xs text-slate-500">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="products">
        <TabsList data-testid="admin-tabs">
          <TabsTrigger value="products" data-testid="admin-tab-products">Products</TabsTrigger>
          <TabsTrigger value="caregivers" data-testid="admin-tab-caregivers">Caregivers</TabsTrigger>
          <TabsTrigger value="requests" data-testid="admin-tab-requests">Care Requests</TabsTrigger>
          <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
        </TabsList>

        {/* PRODUCTS */}
        <TabsContent value="products" className="mt-6 grid lg:grid-cols-[360px_1fr] gap-8">
          <form onSubmit={createProduct} data-testid="admin-product-form" className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm space-y-3 self-start">
            <h3 className="font-display font-semibold text-blue-950 flex items-center gap-2"><Plus className="w-4 h-4" /> Add product</h3>
            <div><Label>Name</Label><Input required data-testid="product-name-input" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Category</Label><Input required value={pForm.category} onChange={(e) => setPForm({ ...pForm, category: e.target.value })} className="mt-1" /></div>
              <div><Label>Type</Label><Input value={pForm.type} onChange={(e) => setPForm({ ...pForm, type: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Price ($)</Label><Input required type="number" step="0.01" data-testid="product-price-input" value={pForm.price} onChange={(e) => setPForm({ ...pForm, price: e.target.value })} className="mt-1" /></div>
              <div><Label>Badge</Label><Input value={pForm.badge} onChange={(e) => setPForm({ ...pForm, badge: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Image URL</Label><Input required value={pForm.image} onChange={(e) => setPForm({ ...pForm, image: e.target.value })} className="mt-1" /></div>
            <div><Label>Short blurb</Label><Input value={pForm.short} onChange={(e) => setPForm({ ...pForm, short: e.target.value })} className="mt-1" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={pForm.description} onChange={(e) => setPForm({ ...pForm, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Download URL</Label><Input value={pForm.download_url} onChange={(e) => setPForm({ ...pForm, download_url: e.target.value })} className="mt-1" /></div>
            <Button type="submit" disabled={saving} data-testid="product-save-button" className="w-full bg-blue-900 hover:bg-blue-950 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Product"}</Button>
          </form>
          <div className="space-y-3">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-blue-900/5">
                <img src={p.image} alt={p.name} className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-blue-950 truncate">{p.name}</p><p className="text-xs text-slate-500">{p.category} · ${p.price.toFixed(2)}</p></div>
                <button onClick={() => deleteProduct(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CAREGIVERS */}
        <TabsContent value="caregivers" className="mt-6 grid lg:grid-cols-[360px_1fr] gap-8">
          <form onSubmit={createCaregiver} data-testid="admin-caregiver-form" className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm space-y-3 self-start">
            <h3 className="font-display font-semibold text-blue-950 flex items-center gap-2"><Plus className="w-4 h-4" /> Add caregiver</h3>
            <div><Label>Name</Label><Input required data-testid="caregiver-name-input" value={cForm.name} onChange={(e) => setCForm({ ...cForm, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Specialties (comma separated)</Label><Input value={cForm.specialties} onChange={(e) => setCForm({ ...cForm, specialties: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Location</Label><Input value={cForm.location} onChange={(e) => setCForm({ ...cForm, location: e.target.value })} className="mt-1" /></div>
              <div><Label>Rate ($/hr)</Label><Input type="number" data-testid="caregiver-rate-input" value={cForm.hourly_rate} onChange={(e) => setCForm({ ...cForm, hourly_rate: e.target.value })} className="mt-1" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Years</Label><Input type="number" value={cForm.years} onChange={(e) => setCForm({ ...cForm, years: e.target.value })} className="mt-1" /></div>
              <div><Label>Rating</Label><Input type="number" step="0.1" value={cForm.rating} onChange={(e) => setCForm({ ...cForm, rating: e.target.value })} className="mt-1" /></div>
              <div><Label>Reviews</Label><Input type="number" value={cForm.reviews} onChange={(e) => setCForm({ ...cForm, reviews: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Image URL</Label><Input required value={cForm.image} onChange={(e) => setCForm({ ...cForm, image: e.target.value })} className="mt-1" /></div>
            <div><Label>Bio</Label><Textarea rows={3} value={cForm.bio} onChange={(e) => setCForm({ ...cForm, bio: e.target.value })} className="mt-1" /></div>
            <Button type="submit" disabled={saving} data-testid="caregiver-save-button" className="w-full bg-blue-900 hover:bg-blue-950 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Caregiver"}</Button>
          </form>
          <div className="space-y-3">
            {caregivers.map((c) => (
              <div key={c.id} data-testid={`admin-caregiver-${c.id}`} className="flex items-center gap-3 bg-white rounded-xl p-3 border border-blue-900/5">
                <img src={c.image} alt={c.name} className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-blue-950 truncate">{c.name}</p><p className="text-xs text-slate-500 truncate">{c.specialties.join(", ")}</p></div>
                <button onClick={() => deleteCaregiver(c.id)} data-testid={`delete-caregiver-${c.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* REQUESTS */}
        <TabsContent value="requests" className="mt-6 space-y-3">
          {requests.length === 0 && <p className="text-slate-500 py-10 text-center">No care requests yet.</p>}
          {requests.map((r) => (
            <div key={r.id} data-testid={`admin-request-${r.id}`} className="bg-white rounded-xl p-4 border border-blue-900/5">
              <div className="flex items-center justify-between"><p className="font-medium text-blue-950">{r.name} · {r.care_type}</p><span className="text-xs bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full">{r.status}</span></div>
              <p className="text-sm text-slate-500 mt-1">{r.email} · {r.phone}</p>
              {r.message && <p className="text-sm text-slate-600 mt-2">{r.message}</p>}
            </div>
          ))}
        </TabsContent>

        {/* MESSAGES */}
        <TabsContent value="messages" className="mt-6 space-y-3">
          {messages.length === 0 && <p className="text-slate-500 py-10 text-center">No messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} data-testid={`admin-message-${m.id}`} className="bg-white rounded-xl p-4 border border-blue-900/5">
              <p className="font-medium text-blue-950">{m.name} <span className="text-sm text-slate-400 font-normal">&lt;{m.email}&gt;</span></p>
              <p className="text-sm text-slate-600 mt-2">{m.message}</p>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
